import React from "react";
import {
  Image,
  Linking,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Icon, IconButton, Text } from "react-native-paper";
import { FieldDisplayRendererProps } from "./types";
import { isImage, isVideo, isAudio } from "../../../utils/mediaUtils";
type Attachment = {
  uri: string;
  name: string;
  mimeType: string;
};

export const FileDisplayRenderer = ({
  value,
  t,
  openMediaViewer,
  showFileName = true,
}: FieldDisplayRendererProps) => {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return null;
  }

  const attachments = value as Attachment[];
  const firstImage =
    attachments.find((attachment) => isImage(attachment.mimeType)) || null;
  const imageUri = firstImage?.uri;
  const imageName = firstImage?.name || t("Photo");

  if (imageUri) {
    return (
      <TouchableOpacity
        style={styles.firstImageRow}
        accessibilityLabel={imageName}
        onPress={() =>
          openMediaViewer({
            uri: imageUri,
            name: imageName,
            mimeType: firstImage?.mimeType,
          })
        }
      >
        <View style={styles.listImageWrapper}>
          <Image source={{ uri: imageUri }} style={styles.listImagePreview} />
        </View>
        {showFileName ? (
          <Text style={styles.firstImageName} numberOfLines={2}>
            {imageName}
          </Text>
        ) : null}
      </TouchableOpacity>
    );
  }

  return (
    <View style={{ marginVertical: 8 }}>
      {showFileName ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          }}
        >
          <View style={{ transform: [{ rotate: "135deg" }] }}>
            <Icon size={24} source="attachment" />
          </View>
          <Text variant="titleMedium">{t("Attachments")}</Text>
          <Text style={styles.attachmentCount}>({value.length})</Text>
        </View>
      ) : null}
      <View>
        {attachments.map((file, index: number) => (
          <TouchableOpacity
            key={index}
            onPress={() => {
              if (
                isImage(file.mimeType) ||
                isVideo(file.mimeType) ||
                isAudio(file.mimeType)
              ) {
                openMediaViewer({
                  uri: file.uri,
                  name: file.name,
                  mimeType: file.mimeType,
                });
              } else {
                Linking.openURL(file.uri);
              }
            }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              borderRadius: 10,
              gap: 6,
            }}
          >
            <IconButton
              icon={
                isImage(file.mimeType)
                  ? "image"
                  : isVideo(file.mimeType)
                    ? "video"
                    : isAudio(file.mimeType)
                      ? "volume-high"
                      : "file"
              }
              size={32}
              style={{ margin: 0, padding: 0, width: 32 }}
            />
            {showFileName ? (
              <View style={{ flexShrink: 1 }}>
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: "500",
                  }}
                  numberOfLines={1}
                >
                  {file.name || `${t("File")} ${index + 1}`}
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                  }}
                >
                  {file.mimeType}
                </Text>
              </View>
            ) : null}
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

export const SignatureDisplayRenderer = ({
  value,
  t,
  openMediaViewer,
}: FieldDisplayRendererProps) => {
  return (
    <TouchableOpacity
      onPress={() =>
        openMediaViewer({
          uri: value,
          mimeType: "image/*",
          name: t("Signature"),
        })
      }
    >
      <Image source={{ uri: value }} style={styles.image} />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  image: {
    width: 100,
    height: 100,
    marginVertical: 8,
  },
  firstImageRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginVertical: 4,
  },
  listImageWrapper: {
    width: 56,
    height: 56,
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#F3F4F6",
  },
  listImagePreview: {
    width: "100%",
    height: "100%",
  },
  firstImageName: {
    flex: 1,
    fontSize: 14,
  },
  attachmentCount: {
    fontSize: 12,
    color: "#666",
    fontWeight: "500",
  },
});
